import React, { useMemo } from "react";
import {
  CountryStats,
  StateOverviewStat,
  StatsParams,
  StatsParamsSetter,
} from "../CountryStats";
import { District, State } from "@digital-girls-hub/shared-resources/models/db";
import StatDatePicker from "./StatDatePicker";
import _ from "lodash";

type StatsProps = {
  statParams: StatsParams;
  setStatsParams: StatsParamsSetter;
  readonlyStateSelection?: boolean;
  requiredData: {
    states: State[];
    districts: District[];
  };
  statsData: {
    stateOverviewStats: StateOverviewStat[];
    countryStats: CountryStats[];
  };
};

type FormattedStats = {
  learnerCount: number;
  skilledCount: number;
  vleCount: number;
  placedCount: number;
};

export default function Stats({
  statParams,
  setStatsParams,
  readonlyStateSelection,
  requiredData: { states, districts },
  statsData: { stateOverviewStats, countryStats },
}: StatsProps) {
  const currentState = useMemo(() => {
    if (states && statParams.stateId && stateOverviewStats) {
      const state = states.find((s) => Number(s.id) === statParams.stateId);
      const stateStat = stateOverviewStats.find(
        (e) => e.stateId === statParams.stateId
      );
      return { ...state, stats: stateStat };
    }
  }, [states, stateOverviewStats, statParams.stateId]);

  const selectedOverviewStats = useMemo(() => {
    if (currentState?.stats) {
      return [currentState.stats];
    }
    return stateOverviewStats;
  }, [currentState?.stats, stateOverviewStats]);

  const allStats = useMemo<FormattedStats>(
    () => getFormattedCountryStats(countryStats, selectedOverviewStats),
    [countryStats, selectedOverviewStats]
  );

  return (
    <div className="w-full md:w-1/2 bg-gray-100">
      <div className="w-full min-h-full flex flex-col justify-center p-5 gap-y-5 max-w-[700px] mx-auto">
        <span className="text-3xl font-semibold mb-5">Onboarding Report</span>
        {/*
      
      


      Filters


      
      
      */}
        <div className="grid grid-cols-2 gap-x-4 w-full mb-5">
          <select
            className="font-bold text-xl rounded-lg px-4 py-2 border-2 border-gray-300"
            value={statParams.stateId}
            onChange={(e) => setStatsParams("stateId")(Number(e.target.value))}
            disabled={readonlyStateSelection}
          >
            {!readonlyStateSelection && <option value={""}>All states</option>}
            {states.map((s) => (
              <option key={s.id} value={Number(s.id)}>
                {s.name}
              </option>
            ))}
          </select>
          {currentState && (
            <select
              className="font-bold text-xl rounded-lg px-4 py-2 border-2 border-gray-300"
              value={statParams.districtId}
              onChange={(e) =>
                setStatsParams("districtId")(Number(e.target.value))
              }
            >
              <option value={""}>All districts</option>
              {districts.map((d) => (
                <option key={d.id} value={Number(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 w-full mb-5">
          <div className="flex items-center gap-x-2">
            <span className="font-semibold">From</span>
            <StatDatePicker
              value={statParams?.dateRange?.from}
              onChange={(v) => {
                _(statParams).set("dateRange.from", v);
                setStatsParams("dateRange")({ ...statParams.dateRange });
              }}
            />
          </div>
          <div className="flex items-center gap-x-2">
            <span className="font-semibold">To</span>
            <StatDatePicker
              value={statParams?.dateRange?.to}
              onChange={(v) => {
                _(statParams).set("dateRange.to", v);
                setStatsParams("dateRange")({ ...statParams.dateRange });
              }}
              minValue={statParams?.dateRange?.from}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Total Learners", value: allStats.learnerCount },
            { label: "Total Skilled", value: allStats.skilledCount },
            { label: "Total VLEs", value: allStats.vleCount },
            { label: "Total Placed", value: allStats.placedCount },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl bg-white border border-gray-200 px-4 py-5"
            >
              <div className="text-sm font-medium text-gray-500">
                {stat.label}
              </div>
              <div className="mt-2 text-3xl font-semibold text-blue">
                {stat.value}
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

const getFormattedCountryStats = (
  countryStats: CountryStats[],
  stateOverviewStats: StateOverviewStat[],
): FormattedStats => ({
  learnerCount: countryStats.reduce((acc, cs) => acc + cs.onboardingCount, 0),
  skilledCount: countryStats.reduce(
    (acc, cs) => acc + cs.courseCompletionCount,
    0,
  ),
  vleCount: stateOverviewStats.reduce((acc, stat) => acc + (stat.vleCount || 0), 0),
  placedCount: stateOverviewStats.reduce(
    (acc, stat) => acc + (stat.placedCount || 0),
    0,
  ),
});
